'use client'

import { langPrefix } from '@/lib/locale-path';
import { notify } from '@/lib/dialogs';
import { resetHints } from '@/lib/hints-store';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
// Prefetched on intent, not on sight (components/IntentLink.tsx).
import Link from '@/components/IntentLink';
import dynamic from 'next/dynamic';
import ReportProblemButton from './feedback/ReportProblemButton';
import ContactUsLink from './feedback/ContactUsLink';
import { askForRating } from '@/lib/rating-prompt';

// The tax rules panel is fetched when someone opens it, not with every page.
const CraComplianceModal = dynamic(() => import('./CraComplianceModal'), { ssr: false });

export default function Footer({ 
  lang = 'en', 
  customSettings,
  rentalLinks = [],
}: { 
  lang?: string; 
  customSettings?: Record<string, { val_en: string; val_fr: string }>;
  /** All rentals and the cities with the most of them (rental SEO, step 5). */
  rentalLinks?: { label: string; href: string }[];
}) {
  const isFr = lang === 'fr';
  const [isComplianceOpen, setIsComplianceOpen] = useState(false);

  // Every item in the footer is a plain text link of one style: the groups'
  // headings say what they are, so no chip, border or icon has to.
  const linkClass =
    'py-3 sm:py-1 text-left text-neutral-200 hover:text-champagne transition-colors underline-offset-4 hover:underline cursor-pointer';

  const getVal = (key: string, defaultVal: string) => {
    if (customSettings && customSettings[key]) {
      const entry = customSettings[key];
      const val = isFr ? entry.val_fr : entry.val_en;
      return val || defaultVal;
    }
    return defaultVal;
  };

  const aboutText = getVal(
    'footer_about', 
    isFr 
      ? 'Des propriétés partout au Canada, directement des propriétaires et des agences.'
      : 'Homes across Canada, straight from owners and agencies.'
  );

  // L-13. The year used to be typed here, so the footer would still claim
  // the previous one all through January and beyond, on a site that otherwise
  // looks maintained.
  const year = new Date().getFullYear();
  const copyrightText = getVal(
    'footer_copyright',
    isFr
      ? `© ${year} Real Estate Channel`
      : `© ${year} Real Estate Channel`
  );

  // The mobile bar is fixed, so the last thing on the page has to leave room
  // for it or it sits underneath. This was already happening before the chips:
  // the CRA link was under the bar on any page short enough not to scroll past
  // it. 84px is the bar plus the safe area.
  // Owner's request, 2026-09-23: everything the footer offered, kept, but
  // grouped and compact - 474px tall on a phone before, 243 now. A phone
  // shows each group as a row (its name, then its links); a wide screen as
  // three columns beside the name of the site.
  const groups: { title: string; items: React.ReactNode[] }[] = [
    ...(rentalLinks.length
      ? [{
          title: isFr ? 'Locations' : 'Rentals',
          items: rentalLinks.map((l) => (
            <Link key={l.href} href={l.href} className={linkClass}>{l.label}</Link>
          )),
        }]
      : []),
    {
      title: isFr ? 'Outils' : 'Tools',
      items: [
        <Link key="calc" href={lang === 'fr' ? '/fr/calculatrices' : `${langPrefix(lang)}/calculators`} className={linkClass}>
          {isFr ? 'Calculateurs' : 'Calculators'}
        </Link>,
        <button key="cra" type="button" onClick={() => setIsComplianceOpen(true)} className={linkClass}>
          {isFr ? 'Règles de l’ARC' : 'CRA rules'}
        </button>,
      ],
    },
    {
      title: isFr ? 'Agences' : 'Agencies',
      items: [
        <Link key="partner" href={`${langPrefix(lang)}/partner`} className={linkClass}>
          {isFr ? 'Pour les agences' : 'For agencies'}
        </Link>,
        <Link key="cabinet" href={`${langPrefix(lang)}/cabinet/realtor/settings`} className={linkClass}>
          {isFr ? 'Mon cabinet' : 'My cabinet'}
        </Link>,
      ],
    },
    {
      title: isFr ? 'Aide' : 'Help',
      items: [
        <ContactUsLink key="contact" lang={lang} className={linkClass} />,
        // On every page, so a wrong figure or a broken button can be reported
        // where it was seen; the report carries the address.
        <ReportProblemButton key="report" lang={lang} short className={linkClass} />,
        <button key="rate" type="button" onClick={() => askForRating('footer', true)} className={linkClass}>
          {isFr ? 'Évaluer le site' : 'Rate the site'}
        </button>,
        <button key="tips" type="button" onClick={() => { resetHints(); notify(isFr ? 'Les astuces seront de nouveau affichées.' : 'The tips will be shown again.', 'success'); }} className={linkClass}>
          {isFr ? 'Réafficher les astuces' : 'Show the tips again'}
        </button>,
      ],
    },
  ];

  return (
    <footer className="render-later w-full bg-neutral-950 text-neutral-400 border-t border-neutral-900 mt-4 pb-[84px] md:pb-0" id="global-application-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-5 pb-4">
        <div className="md:flex md:items-start md:justify-between md:gap-8">
          {/* The site's name and what it is, on a wide screen only (owner,
              2026-09-24): a phone has just seen both in the header and the
              page, and the footer there is for its links. */}
          <div className="hidden md:block min-w-0">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-neutral-900 border border-neutral-800 flex items-center justify-center text-champagne font-bold text-xs">
                R
              </div>
              <span className="font-display font-bold text-white text-[15px] tracking-tight">Real Estate Channel</span>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-neutral-400 max-w-md">{aboutText}</p>
          </div>

          <div className="grid gap-2.5 md:grid-cols-[repeat(4,auto)] md:gap-x-10 md:gap-y-0">
            {groups.map((group) => (
              <div key={group.title} className="grid grid-cols-[76px_1fr] items-baseline md:block">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-400 md:mb-1.5">{group.title}</h2>
                <div className="flex flex-wrap gap-x-3.5 text-sm md:flex-col md:items-start md:text-[13px]">
                  {group.items}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-neutral-900 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-neutral-400 [&_a]:py-1">
          <span>{copyrightText}</span>
          {/* The three legal links move as one: when the line is too narrow
              they go to the next line together, never one of them alone. */}
          <span className="inline-flex gap-x-3.5 whitespace-nowrap">
            <Link href={`${langPrefix(lang)}/terms`} className="inline-block py-1.5 text-neutral-400 hover:text-champagne transition-colors underline-offset-4 hover:underline">
              {isFr ? 'Conditions' : 'Terms'}
            </Link>
            <Link href={`${langPrefix(lang)}/privacy`} className="inline-block py-1.5 text-neutral-400 hover:text-champagne transition-colors underline-offset-4 hover:underline">
              {isFr ? 'Confidentialité' : 'Privacy'}
            </Link>
            {/* A statement nobody can find is not a statement. The footer is
                where a visitor looking for one expects it to be. */}
            <Link href={`${langPrefix(lang)}/accessibility`} className="inline-block py-1.5 text-neutral-400 hover:text-champagne transition-colors underline-offset-4 hover:underline">
              {isFr ? 'Accessibilité' : 'Accessibility'}
            </Link>
          </span>
        </div>
      </div>

      {/* CRA Compliance Modal */}
      {/* Into <body>, not here: the footer is laid out only when it scrolls
          into view (render-later, content-visibility), which also makes it
          the box a fixed-position child is drawn in - the panel was clipped
          to the footer instead of covering the screen. */}
      {isComplianceOpen &&
        createPortal(<CraComplianceModal isOpen onClose={() => setIsComplianceOpen(false)} lang={lang} />, document.body)}
    </footer>
  );
}
